import { useAtom } from 'jotai';
import { userAtom } from '../store/atoms';
import {
  Role,
  hasRole,
  canSearchUserEmails,
  canUpdateOwnUserProfile,
  canChangeEntryStatus,
  ROLES_ACTIVITY_LOGS,
  ROLES_COMMENT_INTERACTION,
  ROLES_CONTENT_EDITOR,
} from '../constants/roles';

export const useAuth = () => {
  const [user] = useAtom(userAtom);

  const isAuthenticated = !!user;
  const isGuest = user?.role === Role.Guest;
  const isAnonymous = !user;

  /** Entry delete / bulk delete — JSDoc: Admin, SuperAdmin, Editor */
  const canDelete =
    user && hasRole(user.role, ...ROLES_CONTENT_EDITOR);

  /** New file/folder, uploads — JSDoc: Admin, SuperAdmin, Editor */
  const canCreate =
    user && hasRole(user.role, ...ROLES_CONTENT_EDITOR);

  /** POST /api/File/comment — JSDoc: Admin, SuperAdmin, Editor, Commenter */
  const canComment =
    user && hasRole(user.role, ...ROLES_COMMENT_INTERACTION);

  const canResolve =
    user && hasRole(user.role, ...ROLES_COMMENT_INTERACTION);

  /** Thread/replies visibility for logged-in project members (excludes Guest) */
  const canShowAllComments =
    user &&
    hasRole(
      user.role,
      Role.Admin,
      Role.SuperAdmin,
      Role.Editor,
      Role.Commenter,
      Role.Viewer
    );

  /** POST /api/File/comment/pin — JSDoc: Admin, SuperAdmin, Editor, Commenter */
  const canPinComment =
    user && hasRole(user.role, ...ROLES_COMMENT_INTERACTION);

  const canSearchUserEmailsForShare =
    user && canSearchUserEmails(user.role);

  const canUpdateOwnProfile = !!user && canUpdateOwnUserProfile(user.role);

  /** GET /api/File/{id}/activity-logs — JSDoc: Admin, SuperAdmin */
  const canViewFileActivityLogs =
    user && hasRole(user.role, ...ROLES_ACTIVITY_LOGS);

  /** POST /api/Entry/change-status — JSDoc: Admin, SuperAdmin, Editor, Commenter */
  const canChangeStatus = !!user && canChangeEntryStatus(user.role);

  return {
    user,
    isAuthenticated,
    isGuest,
    isAnonymous,
    canDelete,
    canCreate,
    canComment,
    canResolve,
    canShowAllComments,
    canPinComment,
    canSearchUserEmails: canSearchUserEmailsForShare,
    canUpdateOwnProfile,
    canViewFileActivityLogs,
    canChangeStatus,
  };
};
